import type { CSSProperties, ReactElement, ReactNode } from 'react';
import { useT } from '../lib/i18n';
import { useTip } from './Tip';

interface ClientTagProps {
  readonly name: string;
  /** Cor do cliente, vinda do contrato. E a chave visual do inbox unificado. */
  readonly color: string;
  readonly size?: 'sm' | 'md';
  /**
   * `plain` remove o fundo e a borda, deixando ponto colorido + texto neutro.
   *
   * Numa LISTA, a pilula tingida repete a cor que o trilho da esquerda ja diz, e
   * vinte e oito linhas de pilula colorida viram parede. Fora da lista — no
   * cabecalho de uma conversa aberta, onde ha uma so — a versao tingida ainda
   * ganha: ali a cor identifica em vez de competir.
   */
  readonly variant?: 'solid' | 'plain';
  /** Toma o lugar do ponto: o ícone do piloto, na cor do cliente. */
  readonly marca?: ReactNode;
}

/**
 * De qual cliente esta pessoa vem. Num inbox que junta os pacientes de todos os medicos,
 * este selo e o que impede a atendente de responder em nome do medico errado — por isso
 * ele aparece na LINHA da lista, e nao so na conversa aberta.
 *
 * A cor do cliente vira um tom: fundo bem lavado e texto escurecido na mesma matiz, para
 * o selo continuar legivel com qualquer cor que o cadastro tenha escolhido.
 */
export function ClientTag({
  name,
  color,
  size = 'sm',
  variant = 'solid',
  marca,
}: ClientTagProps): ReactElement {
  const t = useT();
  const tip = useTip();
  const style: CSSProperties =
    variant === 'plain'
      ? {}
      : {
          background: `color-mix(in srgb, ${color} 13%, #fff)`,
          color: `color-mix(in srgb, ${color} 55%, #0f172a)`,
          borderColor: `color-mix(in srgb, ${color} 28%, #fff)`,
        };

  return (
    <span
      className={`ctag ctag--${size}${variant === 'plain' ? ' ctag--plain' : ''}`}
      style={style}
      {...tip(t.inbox.clientTag(name))}
    >
      {marca ?? <span className="ctag__dot" style={{ background: color }} aria-hidden="true" />}
      <span className="ctag__name">{name}</span>
    </span>
  );
}
